/**
 * pdf-page-numbering.js — the numbering RULES, shared verbatim by the page and
 * the worker.
 *
 * Why this file exists: /add-page-numbers used to compute the label twice —
 * once in the blade for the preview, once in the worker for the output. They
 * drifted, and the drift landed on the DEFAULT setting: picking «Страница 1 из
 * 10» drew «1» in the preview and «Страница 1 из 10» in the file. A preview
 * that lies about the default is worse than no preview, because it is believed.
 *
 * So the label text, the section→page assignment and the x/y placement live
 * here, in a plain script that loads in BOTH contexts (importScripts in the
 * worker, <script src> on the page). Neither side owns a second copy.
 *
 * No pdf-lib dependency on purpose: the page must be able to load this without
 * pulling ~1 MB of pdf-lib in for a preview. Text WIDTH is the one thing this
 * file cannot know (it is a font metric) — callers pass it in, and the worker
 * measures it with the very font it is about to draw with.
 *
 * Depends on: pdf-doc-helpers.js (parsePageRange) — load that first.
 */
(function (global) {
    'use strict';

    var H = global.PdfDocHelpers;

    /* ── numeral styles ──────────────────────────────────────────────────── */

    function toRoman(n) {
        if (!(n > 0)) return String(n);
        var map = [['m', 1000], ['cm', 900], ['d', 500], ['cd', 400], ['c', 100], ['xc', 90],
                   ['l', 50], ['xl', 40], ['x', 10], ['ix', 9], ['v', 5], ['iv', 4], ['i', 1]];
        var s = '';
        for (var i = 0; i < map.length; i++) {
            while (n >= map[i][1]) { s += map[i][0]; n -= map[i][1]; }
        }
        return s;
    }

    /* a, b, … z, aa, ab — spreadsheet-style, used for legal exhibit labels. */
    function toLetters(n) {
        if (!(n > 0)) return String(n);
        var s = '';
        while (n > 0) {
            var r = (n - 1) % 26;
            s = String.fromCharCode(97 + r) + s;
            n = Math.floor((n - 1) / 26);
        }
        return s;
    }

    var STYLES = {
        'arabic':       function (n, pad) { return padNum(String(n), pad); },
        'roman-lower':  function (n)      { return toRoman(n); },
        'roman-upper':  function (n)      { return toRoman(n).toUpperCase(); },
        'letter-lower': function (n)      { return toLetters(n); },
        'letter-upper': function (n)      { return toLetters(n).toUpperCase(); },
    };

    /* Zero-padding is what turns a page number into a Bates number
       (ACME-000001). Only meaningful for arabic. */
    function padNum(s, pad) {
        pad = Math.max(0, Math.min(15, parseInt(pad, 10) || 0));
        while (s.length < pad) s = '0' + s;
        return s;
    }

    function styleNumber(style, n, pad) {
        var fn = STYLES[style] || STYLES.arabic;
        return fn(n, pad);
    }

    /* ── label pattern ───────────────────────────────────────────────────── */

    /**
     * Render one label. Tokens: {n} {total} {sectionTotal} {filename}.
     * A pattern with no {n} still gets stamped — some users want a bare running
     * header — so an empty result, not a missing token, is what suppresses it.
     */
    function renderPattern(pattern, ctx) {
        var p = String(pattern == null ? '{n}' : pattern);
        var n = styleNumber(ctx.style, ctx.n, ctx.pad);
        return p
            .replace(/\{n\}/g, n)
            .replace(/\{total\}/g, styleNumber(ctx.style, ctx.total, 0))
            .replace(/\{sectionTotal\}/g, styleNumber(ctx.style, ctx.sectionTotal, 0))
            .replace(/\{filename\}/g, String(ctx.filename || ''));
    }

    /* ── section → page assignment ───────────────────────────────────────── */

    /**
     * Which pages get numbered, and what each one says.
     *
     * `range`    — global gate: pages that get a number at all ('' = every page).
     * `sections` — [{ from, pattern, style, start, pad }], `from` is 1-based.
     *              Page i belongs to the LAST section whose `from` <= i+1.
     *              The per-section counter advances only over pages that
     *              actually get numbered, so excluding the cover does not
     *              silently burn number 1 on it.
     *
     * Returns Array(pageCount) of string|null (null = this page stays clean).
     * Throws RangeError('BAD_RANGE: …') from parsePageRange on a bad range.
     */
    function resolveLabels(opts) {
        var pageCount = Math.max(0, parseInt(opts.pageCount, 10) || 0);
        var labels = new Array(pageCount).fill(null);
        if (!pageCount) return labels;

        var wanted = null;
        var raw = String(opts.range || '').trim();
        if (raw && raw.toLowerCase() !== 'all') {
            if (!H || typeof H.parsePageRange !== 'function') throw new Error('MISSING_HELPERS');
            var idx = H.parsePageRange(raw, pageCount);
            wanted = new Set(idx);
        }

        var secs = (opts.sections && opts.sections.length ? opts.sections : [{ from: 1 }])
            .map(function (s, i) {
                return {
                    from: Math.max(1, Math.min(pageCount, parseInt(s.from, 10) || 1)),
                    pattern: s.pattern == null ? '{n}' : s.pattern,
                    style: s.style || 'arabic',
                    start: parseInt(s.start, 10),
                    pad: parseInt(s.pad, 10) || 0,
                    ord: i,
                };
            })
            .sort(function (a, b) { return a.from - b.from || a.ord - b.ord; });
        if (secs.length) secs[0].from = 1;   // the first section always owns page 1 onward

        /* Which section owns each page, then a counter per section. */
        var owner = new Array(pageCount).fill(0);
        for (var i = 0, s = 0; i < pageCount; i++) {
            while (s + 1 < secs.length && secs[s + 1].from <= i + 1) s++;
            owner[i] = s;
        }

        var sectionTotals = new Array(secs.length).fill(0);
        for (var j = 0; j < pageCount; j++) {
            if (wanted && !wanted.has(j)) continue;
            sectionTotals[owner[j]]++;
        }

        var counters = secs.map(function (sec) {
            return Number.isFinite(sec.start) ? sec.start : 1;
        });

        for (var k = 0; k < pageCount; k++) {
            if (wanted && !wanted.has(k)) continue;
            var sec = secs[owner[k]];
            labels[k] = renderPattern(sec.pattern, {
                n: counters[owner[k]],
                total: pageCount,
                sectionTotal: sectionTotals[owner[k]],
                style: sec.style,
                pad: sec.pad,
                filename: opts.filename,
            });
            counters[owner[k]]++;
        }
        return labels;
    }

    /* ── placement ───────────────────────────────────────────────────────── */

    var POSITIONS = ['top-left', 'top-center', 'top-right',
                     'middle-left', 'middle-center', 'middle-right',
                     'bottom-left', 'bottom-center', 'bottom-right'];

    /**
     * On a bound document the number sits on the OUTER edge, so it flips side
     * every turn: page 1 (recto) right, page 2 (verso) left. Acrobat Pro makes
     * you run the whole operation twice to get this; it is one boolean here.
     * Centre positions have no outer edge to follow and stay put.
     */
    function mirrorPosition(position, pageIndex, facing) {
        if (!facing) return position;
        if (pageIndex % 2 === 0) return position;          // 1-based odd = recto = as configured
        var parts = String(position).split('-');
        if (parts[1] === 'left') parts[1] = 'right';
        else if (parts[1] === 'right') parts[1] = 'left';
        return parts.join('-');
    }

    /**
     * Baseline origin for the label, in PDF points from the bottom-left corner.
     * `textW`/`textH` come from the caller because only the caller holds the
     * font. Same function drives the preview overlay and the stamped page.
     */
    function placeLabel(position, pageW, pageH, margin, textW, textH) {
        var parts = String(position || 'bottom-center').split('-');
        var v = parts[0], h = parts[1];
        var x = margin, y = margin;
        if (h === 'center') x = (pageW - textW) / 2;
        else if (h === 'right') x = pageW - margin - textW;
        if (v === 'top') y = pageH - margin - textH;
        else if (v === 'middle') y = (pageH - textH) / 2;
        return { x: x, y: y };
    }

    /* ── units ───────────────────────────────────────────────────────────── */

    var MM_PER_PT = 25.4 / 72;
    function mmToPt(mm) { return (Number(mm) || 0) / MM_PER_PT; }
    function ptToMm(pt) { return (Number(pt) || 0) * MM_PER_PT; }

    /* ── colour ──────────────────────────────────────────────────────────── */

    /** '#rrggbb' | '#rgb' → {r,g,b} in 0..1. Invalid input falls back to grey. */
    function parseHexColor(hex, fallback) {
        var s = String(hex == null ? '' : hex).replace(/^#/, '');
        if (s.length === 3) s = s.charAt(0) + s.charAt(0) + s.charAt(1) + s.charAt(1) + s.charAt(2) + s.charAt(2);
        if (!/^[0-9a-fA-F]{6}$/.test(s)) s = String(fallback || '444444').replace(/^#/, '');
        return {
            r: parseInt(s.slice(0, 2), 16) / 255,
            g: parseInt(s.slice(2, 4), 16) / 255,
            b: parseInt(s.slice(4, 6), 16) / 255,
        };
    }

    global.PdfPageNumbering = {
        POSITIONS: POSITIONS,
        STYLE_KEYS: Object.keys(STYLES),
        toRoman: toRoman,
        toLetters: toLetters,
        styleNumber: styleNumber,
        renderPattern: renderPattern,
        resolveLabels: resolveLabels,
        mirrorPosition: mirrorPosition,
        placeLabel: placeLabel,
        mmToPt: mmToPt,
        ptToMm: ptToMm,
        parseHexColor: parseHexColor,
    };
})(typeof self !== 'undefined' ? self : this);
